import { useCallback, useEffect, useRef, useState } from "react";
import { DataService } from "@/services/data";
import { DetailDataResponse } from "@/types/dynamic-detail";
import { useNavigate } from "react-router-dom";

interface UseDynamicDetailOptions {
    objectType: string;
    objectId: string | number;
    endpointUrl?: string;
}

interface UseDynamicDetailResult {
    detailData: DetailDataResponse | null;
    /** Nothing to show yet for this record (its first load). */
    loading: boolean;
    /** The record shown is being fetched again (after an action, say): what is on screen stays meanwhile. */
    refreshing: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

type FetchOutcome = {
    recordKey: string;
    data: DetailDataResponse | null;
    error: string | null;
};

export function useDynamicDetail({ objectType, objectId, endpointUrl }: UseDynamicDetailOptions): UseDynamicDetailResult {
    const objectPath = endpointUrl ?? objectType;
    const recordKey = `${objectPath}/${objectId}`;
    const [outcome, setOutcome] = useState<FetchOutcome | null>(null);
    const [fetching, setFetching] = useState(true);
    const lastRequestRef = useRef(0);
    const navigate = useNavigate();

    // Le state peut encore décrire l'enregistrement précédent : la vue est re-rendue avec le
    // nouvel id avant que l'effet de chargement ne s'exécute. On considère ces données comme
    // absentes, sinon header, tabs, actions et redirection viendraient d'un autre enregistrement.
    const currentOutcome = outcome?.recordKey === recordKey ? outcome : null;
    const detailData = currentOutcome?.data ?? null;
    const error = currentOutcome?.error ?? null;
    // A refetch keeps the record on screen (no blank page after an action): only the first load is a loading.
    const loading = currentOutcome === null;
    const refreshing = fetching && currentOutcome !== null;

    const fetchData = useCallback(async () => {
        const requestId = ++lastRequestRef.current;
        setFetching(true);
        try {
            const data = (await DataService.client
                .url(`/crm/objects/${objectPath}/${objectId}/detail-data/`)
                .get()
                .json()) as DetailDataResponse;

            // Réponse dépassée (enregistrement changé ou refetch plus récent) : ne rien écrire.
            if (lastRequestRef.current !== requestId) return;
            setOutcome({ recordKey, data, error: null });
        } catch (err) {
            console.error("Error fetching dynamic detail data:", err);
            if (lastRequestRef.current !== requestId) return;
            setOutcome({ recordKey, data: null, error: "Erreur lors du chargement des données" });
        } finally {
            if (lastRequestRef.current === requestId) {
                setFetching(false);
            }
        }
    }, [objectPath, objectId, recordKey]);

    useEffect(() => {
        if (objectId) {
            fetchData();
        }
    }, [objectId, fetchData]);

    useEffect(() => {
        if (detailData?.auto_redirect_url) {
            navigate(detailData.auto_redirect_url);
        }
    }, [detailData, navigate]);

    return {
        detailData,
        loading,
        refreshing,
        error,
        refetch: fetchData
    };
}
